import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import logo from '../assets/concreto_tec_logo.png';
import { clearSession, getSession, getSessionRole } from '../services/authSession';

const pageTitles = {
  '/app': 'Dashboard',
  '/app/usuarios': 'Usuários',
  '/app/clientes': 'Clientes',
  '/app/empresas': 'Empresas',
  '/app/contatos': 'Contatos',
  '/app/obras': 'Obras',
  '/app/concretagens': 'Concretagens',
  '/app/gestao-concretagem': 'Gestão de concretagem',
  '/app/moldadores': 'Moldadores',
  '/app/caminhoes': 'Caminhoes',
  '/app/concreteiras': 'Concreteiras',
  '/app/resultados-corpos-prova': 'Resultados',
  '/app/revisao-resultados': 'Revisão de resultados',
  '/app/ficha-ruptura': 'Ficha de ruptura',
  '/app/resultados-sequencial': 'Resultados sequencial',
  '/app/relatorio-caminhao': 'Relatório por caminhão',
  '/app/medicao-moldagem': 'Medição - Moldagem',
  '/app/medicao-ruptura': 'Medição - Ruptura',
};

// Shell das rotas autenticadas: sidebar (colapsável/drawer mobile), header com título da rota e logout, e <Outlet>.
function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const title = pageTitles[location.pathname] || 'Painel';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const role = getSessionRole();

  // Dados do usuário logado exibidos no header.
  const user = getSession()?.user || {};
  const userNome = user.nome || '';
  const userEmail = user.email || '';
  // Selo de acesso derivado do domínio do e-mail: @concreto.tec.br => admin, senão usuario.
  const displayRole = userEmail.toLowerCase().endsWith('@concreto.tec.br') ? 'admin' : 'usuario';

  // Encerra a sessão e volta para a tela de login.
  const handleLogout = () => {
    clearSession();
    navigate('/', { replace: true });
  };

  return (
    <div className="app-shell min-h-screen bg-slate-50">
      <div className="app-frame flex h-screen overflow-hidden">
        <Sidebar
          role={role}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
        />
        <div className="app-content flex-1 overflow-y-auto">
          <header className="app-header sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur">
            <div className="app-header-row flex items-start justify-between gap-4 px-4 py-4 sm:px-6">
              <div className="app-header-left flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(true)}
                  className="app-header-logo inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white p-1 shadow-sm transition hover:border-blue-200 md:hidden"
                  aria-label="Abrir menu"
                >
                  <img src={logo} alt="Logo Kretos Lab" className="h-7 w-7 object-contain" />
                </button>
                <div className="app-header-title">
                  <p className="app-brand text-xs uppercase tracking-[0.2em] text-slate-400">
                    Kretos Lab
                  </p>
                  <h1 className="app-page-title text-xl font-semibold text-slate-900">{title}</h1>
                </div>
              </div>
              <div className="app-header-right ml-auto flex flex-nowrap items-center gap-3 text-sm text-slate-500">
                <div className="text-right">
                  <p className="font-medium text-slate-700">{userNome}</p>
                  <p className="text-xs">{userEmail}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  {displayRole}
                </span>
                <Link
                  to="/"
                  onClick={(event) => {
                    event.preventDefault();
                    handleLogout();
                  }}
                  className="app-header-action rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                >
                  Sair
                </Link>
              </div>
            </div>
          </header>
          <main className="app-main px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
      {isSidebarOpen ? (
        <div className="drawer-root fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            className="drawer-overlay absolute inset-0 h-full w-full bg-slate-900/40"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Fechar menu"
          />
          <Sidebar
            isCollapsed={false}
            role={role}
            className="drawer-panel absolute left-0 top-0 h-full w-72 shadow-xl"
            onClose={() => setIsSidebarOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

export default Layout;
